import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, CheckCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';

const Index: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background to-background/80 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top_right,theme(colors.primary.DEFAULT/15%),transparent_40%)]"></div>
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_bottom_left,theme(colors.primary.DEFAULT/15%),transparent_40%)]"></div>
        
        <div className="container relative z-10 mx-auto max-w-screen-xl">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="relative text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Empower Your Learning Journey
              <div className="absolute -z-10 h-16 w-16 rounded-full bg-primary/10 blur-xl"></div>
            </h1>
            <p className="mt-6 text-xl text-muted-foreground">
              A comprehensive learning platform designed to help you master new skills through
              interactive courses, engaging content, and knowledge validation.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="min-w-[150px]">
                <Link to="/login">Log In</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="bg-secondary/50 px-4 py-16 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-screen-xl">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Designed for Effective Learning
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Our platform combines best practices in education with cutting-edge technology.
            </p>
          </div>
          
          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Feature 1 */}
            <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Book className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-xl font-medium">Structured Courses</h3>
              <p className="mt-2 text-muted-foreground">
                Well-organized learning paths with step-by-step modules designed to build your knowledge systematically.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-xl font-medium">Rich Content</h3>
              <p className="mt-2 text-muted-foreground">
                Diverse learning materials including text, videos, and interactive elements to enhance comprehension.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-xl font-medium">Knowledge Validation</h3>
              <p className="mt-2 text-muted-foreground">
                Interactive quizzes to test your understanding and reinforce learning at every step.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="bg-background px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="container mx-auto max-w-screen-xl">
          <div className="mx-auto max-w-3xl rounded-2xl bg-primary/5 p-8 text-center sm:p-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Ready to Start Learning?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Join our platform today and begin your journey to mastery.
            </p>
            <Button asChild size="lg" className="mt-8">
              <Link to="/login" className="inline-flex items-center">
                Log In <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="mt-auto border-t bg-muted/40 px-4 py-6 sm:px-6 lg:px-8">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center space-x-2">
            <Book className="h-5 w-5 text-primary" />
            <span className="font-medium">
              <span className="text-white">CTWO</span>
              <span className="text-yellow">Learning</span>
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
